import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { supabase } from "../lib/supabaseClient";
import logoImage from "@/assets/legacyloop-white-trans.png";

export default function ResetPassword() {
  const [, setLocation] = useLocation();

  // The recovery email link carries a session in the URL. The Supabase client is
  // created with detectSessionInUrl: true, so it parses that on load and fires a
  // PASSWORD_RECOVERY event. That can happen before this component mounts, so we
  // also check for an existing session on mount. `ready` means we have a valid
  // recovery session and can safely let the user set a new password.
  const [checking, setChecking] = useState(true);
  const [ready, setReady] = useState(false);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setReady(true);
        setChecking(false);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
      setChecking(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Those passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setErr(error.message);
        return;
      }
      setDone(true);
      // Clear the temporary recovery session so the next sign-in is a fresh login.
      await supabase.auth.signOut();
    } catch (e: any) {
      setErr(e?.message || "Could not update your password. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white font-sans">
      <header className="py-6 px-6 border-b border-[#1B263B]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src={logoImage} alt="LegacyLoop" className="h-12 w-auto" />
          <h1 className="text-lg font-bold text-[#E0A458]">Reset Password</h1>
        </div>
      </header>

      <main className="py-16 px-6">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold text-[#E0A458]">Choose a new password</h2>
          <p className="text-slate-300 mt-2">
            Set a new password to get back to your athlete&rsquo;s journey.
          </p>
        </div>

        <div className="mt-8 max-w-md mx-auto">
          {checking ? (
            <p className="text-center text-slate-300">Verifying your reset link&hellip;</p>
          ) : done ? (
            <div className="text-center space-y-6">
              <p className="text-slate-200">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <button
                onClick={() => setLocation("/login")}
                className="w-full rounded-xl bg-[#E0A458] text-black py-3 font-semibold hover:bg-[#f0b467]"
              >
                Continue to sign in
              </button>
            </div>
          ) : ready ? (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block mb-2 text-sm text-slate-300">New password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl bg-[#0f2740] border border-[#1B263B] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E0A458]"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm text-slate-300">Confirm new password</label>
                <input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full rounded-xl bg-[#0f2740] border border-[#1B263B] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E0A458]"
                  placeholder="Re-enter your new password"
                />
              </div>
              {err && <p className="text-red-400 text-sm">{err}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-[#E0A458] text-black py-3 font-semibold hover:bg-[#f0b467] disabled:opacity-60"
              >
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          ) : (
            <div className="text-center space-y-6">
              <p className="text-slate-300">
                This reset link is invalid or has expired. Head back to sign in and choose
                &ldquo;Forgot password?&rdquo; to get a fresh link.
              </p>
              <button
                onClick={() => setLocation("/login")}
                className="w-full rounded-xl bg-[#E0A458] text-black py-3 font-semibold hover:bg-[#f0b467]"
              >
                Back to sign in
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
